import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, InfoTitle, NumberText } from '@common/components';
import { InfoCard } from '@builder';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import {
  StyledAccentColor,
  StyledCardSection,
  StyledNoDataMessage,
  StyledDisplayResults,
} from './TotalResultsCard.styles';
import { TotalResultsCardProps } from './TotalResultsCard.types';
import { Sizes, Weights } from '@/common/types';
import { COMMON_TRANSLATION } from '@/common/constants';
import {
  StyledResultsPercentageText,
  StyledContentSeparator,
  StyledResultsPercentage,
  StyledColumn,
} from '../DiseasesSection.styles';

const TotalResultsCard: FC<TotalResultsCardProps> = ({
  isDataAvailable,
  totalResults,
  totalNegative,
  totalPositive,
  positivePercentage,
  negativePercentage,
}) => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);

  return (
    <InfoCard css={{ flexDirection: 'column' }} hasBorder data-testid="total-results-card">
      <Box css={{ display: 'flex', flexDirection: 'row' }}>
        <StyledCardSection css={{ width: '32%' }}>
          <InfoTitle css={{ marginBottom: '$4' }} testId="total-results-title">
            {t('total_results_delivered')}
          </InfoTitle>
          <StyledAccentColor css={{ backgroundColor: '$blue400' }} />
          {isDataAvailable ? (
            <StyledDisplayResults>
              <NumberText size={Sizes.LARGE} weight={Weights.EXTRA_BOLD} testId="total-results">
                {totalResults}
              </NumberText>
            </StyledDisplayResults>
          ) : (
            <StyledNoDataMessage>{t('not_applicable', { ns: COMMON_TRANSLATION })}</StyledNoDataMessage>
          )}
        </StyledCardSection>
        <StyledCardSection css={{ width: '32%' }}>
          <InfoTitle css={{ marginBottom: '$4' }} testId="total-results-positive-title">
            {t('total_positives')}
          </InfoTitle>
          <StyledAccentColor css={{ backgroundColor: '$yellow400' }} />
          {isDataAvailable ? (
            <StyledDisplayResults>
              <NumberText size={Sizes.MEDIUM} weight={Weights.EXTRA_BOLD} testId="total-positives-results">
                {totalPositive}
              </NumberText>
              <StyledContentSeparator />
              <StyledColumn>
                <StyledResultsPercentage data-testid="total-positives-percentage">
                  {`${positivePercentage}%`}
                </StyledResultsPercentage>
                <StyledResultsPercentageText>{t('of_patients')}</StyledResultsPercentageText>
              </StyledColumn>
            </StyledDisplayResults>
          ) : (
            <StyledNoDataMessage>{t('not_applicable', { ns: COMMON_TRANSLATION })}</StyledNoDataMessage>
          )}
        </StyledCardSection>
        <StyledCardSection css={{ width: '32%' }}>
          <InfoTitle css={{ marginBottom: '$4' }} testId="total-results-negative-title">
            {t('total_negatives')}
          </InfoTitle>
          <StyledAccentColor css={{ backgroundColor: '#62A25A' }} />
          {isDataAvailable ? (
            <StyledDisplayResults>
              <NumberText size={Sizes.MEDIUM} weight={Weights.EXTRA_BOLD} testId="total-negative-results">
                {totalNegative}
              </NumberText>
              <StyledContentSeparator />
              <StyledColumn>
                <StyledResultsPercentage data-testid="total-negatives-percentage">
                  {`${negativePercentage}%`}
                </StyledResultsPercentage>
                <StyledResultsPercentageText>{t('of_patients')}</StyledResultsPercentageText>
              </StyledColumn>
            </StyledDisplayResults>
          ) : (
            <StyledNoDataMessage>{t('not_applicable', { ns: COMMON_TRANSLATION })}</StyledNoDataMessage>
          )}
        </StyledCardSection>
      </Box>
    </InfoCard>
  );
};

export default TotalResultsCard;
